import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

interface AnimatedHeightProps {
  children: ReactNode
  /** Token-driven transition duration. Defaults to 'slow' (220ms). */
  duration?: 'fast' | 'base' | 'slow'
  style?: CSSProperties
}

const DURATION_VAR: Record<NonNullable<AnimatedHeightProps['duration']>, string> = {
  fast: 'var(--motion-duration-fast)',
  base: 'var(--motion-duration-base)',
  slow: 'var(--motion-duration-slow)',
}

/**
 * Smoothly animates its own height to match its content. Use it to wrap a
 * region whose content swaps between states of different heights (e.g. a
 * loading skeleton → result, or a panel that grows when extra rows appear)
 * so the surrounding layout eases open/closed instead of jumping.
 *
 * Measures the inner content with a ResizeObserver and drives an explicit
 * `height` transition. The `--motion-duration-*` tokens collapse to 0ms
 * under `prefers-reduced-motion`, so this becomes an instant swap for
 * users who opted out — no extra handling needed.
 */
export function AnimatedHeight({
  children,
  duration = 'slow',
  style,
}: AnimatedHeightProps): JSX.Element {
  const innerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    const el = innerRef.current
    if (el === null) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setHeight(entry.contentRect.height)
    })
    observer.observe(el)
    setHeight(el.getBoundingClientRect().height)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      style={{
        // First paint (height === null) uses auto so there's no jump before
        // the observer reports a concrete height.
        height: height === null ? 'auto' : height,
        overflow: 'hidden',
        transition: `height ${DURATION_VAR[duration]} var(--motion-ease-out)`,
        ...style,
      }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  )
}
